import { type Ref, computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { Checkout, CheckoutBillingAddress, Screen } from '@/types'
import currenciesData from '@/store/data/currencies'

export const useCheckoutStore = defineStore('CheckoutStore', () => {
  const baseState = {
    country: 'US',
    currency: 'USD',
    firstName: '',
    lastName: '',
    couponCodes: [],
    planCode: '',
    billingInfo: {},
    billingAddress: {} as CheckoutBillingAddress,
    email: '',
    accountCode: '',
    surveyResponses: {},
    isComplete: false,
    isActive: false,
    hasCheckoutStart: null,
    alreadyHas: false,
    paymentRequired: true,
    noPaymentPlanType: ''
  }

  const checkout: Ref<Checkout> = ref(baseState)

  const completedScreens: Ref<Screen[]> = ref([])

  const setCheckoutProperty = (prop: any) => {
    checkout.value = {
      ...checkout.value,
      ...prop
    }
  }

  const setCompletedScreen = (screen: Screen) => {
    completedScreens.value = [...completedScreens.value, screen]
  }

  const setCheckoutCurrency = (country: string) => {
    const countryCurrency = currenciesData.find(currency => currency.countries.includes(country))

    const currency = countryCurrency?.currency || 'USD'

    setCheckoutProperty({ currency })
  }

  const set3DSToken = (token: string | null) => {
    setCheckoutProperty({
      billingInfo: {
        ...billingInfo.value,
        ...(token && { threeDSecureActionResultTokenId: token })
      }
    })
  }

  const clear3DSToken = () => {
    const { threeDSecureActionResultTokenId, ...billingInfoRest } = billingInfo.value

    setCheckoutProperty({
      billingInfo: {
        ...billingInfoRest
      }
    })
  }

  const resetCheckoutState = () => (checkout.value = baseState)

  const alreadyHas = computed(() => checkout.value.alreadyHas)
  const billingAddress = computed(() => checkout.value.billingAddress)
  const billingInfo = computed(() => checkout.value.billingInfo)
  const country = computed(() => checkout.value.country)
  const currency = computed(() => checkout.value.currency)
  const firstName = computed(() => checkout.value.firstName)
  const lastName = computed(() => checkout.value.lastName)
  const planCode = computed(() => checkout.value.planCode)
  const postalCode = computed(() => billingAddress.value?.postalCode)
  const surveyResponses = computed(() => checkout.value.surveyResponses)
  const appReturnLink = computed(() => checkout.value.appReturnLink)
  const isComplete = computed(() => checkout.value.isComplete)
  const isActive = computed(() => checkout.value.isActive)
  const paymentDetails = computed(() => checkout.value.paymentDetails)
  const hasValidPaymentDetails = computed(() => !!paymentDetails.value)
  const requiresPaymentDetails = computed(() => !hasValidPaymentDetails.value)
  const paymentRequired = computed(() => checkout.value.paymentRequired)
  const hasCheckoutStart = computed(() => checkout.value.hasCheckoutStart)
  const noPaymentPlanType = computed(() => checkout.value.noPaymentPlanType)
  return {
    alreadyHas,
    appReturnLink,
    billingAddress,
    billingInfo,
    checkout,
    clear3DSToken,
    completedScreens,
    country,
    currency,
    hasCheckoutStart,
    hasValidPaymentDetails,
    isActive,
    isComplete,
    firstName,
    lastName,
    paymentDetails,
    paymentRequired,
    planCode,
    postalCode,
    resetCheckoutState,
    requiresPaymentDetails,
    set3DSToken,
    setCheckoutCurrency,
    setCheckoutProperty,
    setCompletedScreen,
    surveyResponses,
    noPaymentPlanType
  }
})
